import { Check, Loader2, Pill, Plus, RotateCw } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@ascleo/ui/components/ui/button";
import { cn } from "@ascleo/ui/lib/utils";
import type { PrescriptionSummary } from "../machine";

function formatPrescriptionDate(
  value: string | undefined,
): string | undefined {
  if (!value) return undefined;

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleDateString("de-DE");
}

/**
 * Renders prior-prescription helpers for the eGK-identified composition only.
 * Each earlier prescription is one large tappable row that adds it to the
 * request and then shows it as added (KO-86); a failed load offers a retry
 * (KO-87).
 *
 * @since 1.2.0
 * @example
 * ```tsx
 * <PrescriptionHistorySection
 *   requestText=""
 *   isHistoryLoading={false}
 *   historyError={undefined}
 *   prescriptions={[]}
 *   onAddPrescription={() => undefined}
 *   onRetry={() => undefined}
 * />
 * ```
 */
export function PrescriptionHistorySection({
  requestText,
  isHistoryLoading,
  historyError,
  prescriptions,
  onAddPrescription,
  onRetry,
}: {
  readonly requestText: string | undefined;
  readonly isHistoryLoading: boolean;
  readonly historyError: string | undefined;
  readonly prescriptions: ReadonlyArray<PrescriptionSummary>;
  readonly onAddPrescription: (prescription: PrescriptionSummary) => void;
  readonly onRetry: () => void;
}) {
  const { t } = useTranslation();

  return (
    <div
      data-testid="frontdesk-prescription-history-section"
      className="flex min-w-0 flex-col gap-3 rounded-3xl bg-foreground-soft p-5"
    >
      <div className="flex items-start gap-3">
        <span aria-hidden className="grid size-10 shrink-0 place-items-center rounded-xl bg-primary-subtle text-primary-ink">
          <Pill className="size-6" />
        </span>
        <div className="min-w-0">
          <h3 className="m-0 text-lg font-semibold">
            {t("self-service.prescription.medication-selection.history-title")}
          </h3>
          <p className="m-0 text-base text-muted-foreground">
            {t("self-service.prescription.medication-selection.history-hint-v2")}
          </p>
        </div>
      </div>

      {isHistoryLoading && (
        <output
          data-testid="frontdesk-prescription-history-loading"
          className="flex items-center gap-3 rounded-2xl bg-background px-4 py-3 text-base text-muted-foreground"
        >
          <Loader2 aria-hidden className="size-5 animate-spin" />
          {t("self-service.prescription.medication-selection.history-loading")}
        </output>
      )}

      {historyError && !isHistoryLoading && (
        <div
          role="alert"
          data-testid="frontdesk-prescription-history-error"
          className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-destructive-subtle bg-destructive-soft px-4 py-3 text-base text-foreground"
        >
          <span>{historyError}</span>
          <Button
            type="button"
            variant="outline"
            size="2xl"
            data-testid="frontdesk-prescription-history-retry-btn"
            onClick={onRetry}
          >
            <RotateCw aria-hidden className="size-5" />
            {t("self-service.prescription.medication-selection.history-retry")}
          </Button>
        </div>
      )}

      {prescriptions.length > 0 && !isHistoryLoading && (
        <div className="grid gap-3 md:grid-cols-2">
          {prescriptions.map((prescription, index) => {
            const requestLine = t(
              "self-service.prescription.medication-selection.history-request-line",
              {
                medicationName: prescription.medicationName,
              },
            );
            const isAdded = Boolean(
              requestText
                ?.split("\n")
                .map((line) => line.trim())
                .includes(requestLine),
            );
            const formattedDate = formatPrescriptionDate(
              prescription.prescriptionDate,
            );

            return (
              <button
                key={prescription.id}
                type="button"
                data-testid={`frontdesk-prescription-history-add-${index}-btn`}
                aria-pressed={isAdded}
                disabled={isAdded}
                onClick={() => onAddPrescription(prescription)}
                className={cn(
                  "flex min-h-16 min-w-0 items-center gap-3 rounded-2xl border-2 px-4 py-3 text-left transition-colors outline-none focus-visible:ring-4 focus-visible:ring-ring/45",
                  isAdded
                    ? "border-primary bg-primary-subtle text-primary-ink"
                    : "border-border bg-card text-foreground hover:bg-background",
                )}
              >
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-lg font-semibold">
                    {prescription.medicationName}
                  </span>
                  {formattedDate ? (
                    <span className="block text-base text-muted-foreground">
                      {t(
                        "self-service.prescription.medication-selection.history-date",
                        { date: formattedDate },
                      )}
                    </span>
                  ) : null}
                </span>
                <span className="flex shrink-0 items-center gap-2 text-base font-semibold">
                  {isAdded ? <Check aria-hidden className="size-5" /> : <Plus aria-hidden className="size-5" />}
                  {isAdded
                    ? t("self-service.prescription.medication-selection.history-added")
                    : t("self-service.prescription.medication-selection.history-add")}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
